import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DashboardLayout } from '../components/DashboardLayout';
import { GlobalTaskView } from '../components/GlobalTaskView';
import { PeriodFilter, PeriodRange } from '../components/ui/PeriodFilter';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';

export const TasksPage: React.FC = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState<PeriodRange>({ preset: 'all' });
  const canReassign = ['CEO', 'GROUP_MANAGER', 'MANAGER'].includes(user?.role || '');

  // Fetch team members for reassignment (managers+ only)
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['users-for-reassign'],
    queryFn: () => api.getUsers(),
    staleTime: 60000,
    enabled: canReassign,
  });

  const handleReassign = async (taskId: string, newUserId: string) => {
    try {
      await api.reassignTask(taskId, newUserId);
      // Force page to reload tasks by toggling a key
      window.location.reload();
    } catch (err) {
      console.error('Failed to reassign task:', err);
      alert('Failed to reassign task. Please try again.');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Period Filter */}
        <div className="flex items-center gap-3 flex-wrap pt-2">
          <span className="text-sm font-medium text-gray-700">Due Date:</span>
          <PeriodFilter value={period} onChange={setPeriod} />
        </div>

        {/* Task View */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {user ? (
            <GlobalTaskView
              userId={user.id}
              dueDateFrom={period.dateFrom}
              dueDateTo={period.dateTo}
              onReassign={canReassign ? handleReassign : undefined}
              teamMembers={canReassign ? teamMembers : undefined}
            />
          ) : (
            <div className="text-center py-12 text-gray-500" role="status">
              <p>Loading user information...</p>
            </div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
};